/* @flow */
import React, { useContext } from "react";
import prefixStyles from "../utils/prefixStyles";
import Theme from "../Theme";
import { withColorsProvider, type ColorSet } from "../ColorsProvider";
import { IconThermostat } from "../Icons";
import UserLanguageContext from "../ContextWrappers/UserLanguageContext";

const ValueCallout = (props: ValueCallout) => {
  const { colors, icon, label, value } = props;

  let styledIcon = "";
  let iconStyles = {};
  if (icon) {
    let iconSize = 80;
    let iconStyle = {
      position: "absolute",
      left: -15,
    };

    if (label) {
      iconSize = 50;
      iconStyle.left = 0;
    }

    styledIcon = (
      <IconThermostat
        color={colors.PRIMARY}
        style={iconStyle}
        size={iconSize}
      />
    );

    iconStyles = {
      lineHeight: `${iconSize}px`,
      position: "relative",
      paddingLeft: 50,
    };
  }

  let styles = {
    container: {
      display: "inline-block",
      textAlign: "center",
    },
    value: {
      fontSize: !label && !(value.length > 3) ? 68 : 48,
      textAlign: "center",
      verticalAlign: "middle",
      ...iconStyles,
    },
    label: {
      fontSize: 20,
      whiteSpace: "nowrap",
      textAlign: "center",
    },
  };

  return (
    <span style={styles.container}>
      <span style={styles.value}>
        {styledIcon}
        {value}
      </span>
      <div style={styles.label}>{label}</div>
    </span>
  );
};

type ValueCalloutSmallProps = { value: string, label?: ?string };

const ValueCalloutSmall = (props: ValueCalloutSmallProps) => {
  const { value, label } = props;
  let style = {
    marginBottom: 6,
    fontSize: 16,
  };

  let styledLabel = "";
  if (label) {
    const nbsp = String.fromCharCode(160);
    styledLabel = `${label}:${nbsp}`;
  } else {
    style.fontSize = 20;
  }

  return (
    <div style={style}>
      {styledLabel}
      {value}
    </div>
  );
};

type ThermostatDetailsHeaderProps = {
  colors: ColorSet,
  temperature: number,
  temperatureOutside?: number,
  humidityRelative?: number,
  systemStatus: string,
  compressorSpeed?: number,
};

const ThermostatDetailsHeader = (props: ThermostatDetailsHeaderProps) => {
  const {
    colors,
    temperature,
    temperatureOutside,
    humidityRelative,
    systemStatus,
    compressorSpeed,
  } = props;

  const styles = {
    container: {
      color: colors.PRIMARY,
      background: colors.DETAILS_HEADER_BACKGROUND,
      padding: Theme.Padding.CONTENT,
      lineHeight: "normal",
      flexBasis: "100%",
    },
    calloutContainer: prefixStyles({
      display: "flex",
      justifyContent: "space-around",
      alignItems: "center",
    }),
    smallCallouts: {
      display: "inline-block",
      marginLeft: "1.5em",
      textAlign: "left",
    },
  };

  const languageContext = useContext(UserLanguageContext);
  const language =
    languageContext.localeSet.nx_components.ThermostatDetailsHeader;

  let temperatureValue = `${temperature || "--"}°`;

  let outsideCallout = null;
  if (Number.isFinite(temperatureOutside)) {
    let outsideValue = `${temperatureOutside}°`;
    outsideCallout = (
      <ValueCalloutSmall value={outsideValue} label={language.outside} />
    );
  }

  let humidityCallout = null;
  if (Number.isFinite(humidityRelative)) {
    let humidityValue = `${Math.round(humidityRelative * 100)}%`;
    humidityCallout = (
      <ValueCalloutSmall value={humidityValue} label={language.humidity} />
    );
  }

  let compressorSpeedCallout = null;
  if (Number.isFinite(compressorSpeed)) {
    const compressorSpeedValue = `${Math.round(compressorSpeed * 100)}%`;
    compressorSpeedCallout = (
      <ValueCalloutSmall
        value={compressorSpeedValue}
        label={language.compressorSpeed}
      />
    );
  }

  let systemStatusCallout = null;
  if (systemStatus) {
    systemStatusCallout = <ValueCalloutSmall value={systemStatus} />;
  }

  return (
    <div data-component-name="ThermostatDetailsHeader" style={styles.container}>
      <div style={styles.calloutContainer}>
        <ValueCallout icon value={temperatureValue} colors={colors} />
        {(systemStatusCallout ||
          outsideCallout ||
          humidityCallout ||
          compressorSpeedCallout) && (
          <div style={styles.smallCallouts}>
            {systemStatusCallout}
            {outsideCallout}
            {humidityCallout}
            {compressorSpeedCallout}
          </div>
        )}
      </div>
    </div>
  );
};
export default withColorsProvider(ThermostatDetailsHeader);



// WEBPACK FOOTER //
// ../nx-components/src/Thermostat/ThermostatDetailsHeader.jsx